import {createContext, useCallback, useContext, useEffect, useState, type ReactNode} from "react";
import {
    companyTokenStorage,
    getCompanyMe,
    loginCompany,
    registerCompany,
    type Company,
} from "../services/companyService";

/**
 * Сессия кабинета компаний — намеренно отдельная от AuthContext.
 * Компания и пользователь могут быть залогинены в одном браузере
 * одновременно и не мешать друг другу: разные ключи хранилища и разные
 * типы токена (см. services/companyService.ts).
 */

interface CompanyAuthContextType {
    company: Company | null;
    /** true, пока проверяем сохранённый токен: гарду нельзя решать до этого. */
    loading: boolean;
    isAuthenticated: boolean;
    login: (email: string, password: string) => Promise<string | null>;
    register: (body: {
        name: string;
        email: string;
        password: string;
        phone: string;
        contactName: string;
        inn?: string | null;
        ogrn?: string | null;
        kpp?: string | null;
    }) => Promise<string | null>;
    logout: () => void;
    setCompany: (company: Company) => void;
}

const CompanyAuthContext = createContext<CompanyAuthContextType | undefined>(undefined);

export function CompanyAuthProvider({children}: {children: ReactNode}) {
    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(true);

    // Восстановление сессии по сохранённому токену. Токен может быть просрочен
    // или отозван — тогда чистим хранилище, чтобы гард не крутил спиннер вечно.
    useEffect(() => {
        let cancelled = false;

        const restore = async () => {
            if (!companyTokenStorage.get()) {
                setLoading(false);
                return;
            }
            const r = await getCompanyMe();
            if (cancelled) return;
            if (r.data?.company) {
                setCompany(r.data.company);
            } else {
                companyTokenStorage.clear();
            }
            setLoading(false);
        };

        void restore();
        return () => {
            cancelled = true;
        };
    }, []);

    /** Возвращает текст ошибки или null при успехе — форма сама решает, как показать. */
    const login = useCallback(async (email: string, password: string): Promise<string | null> => {
        const r = await loginCompany(email, password);
        if (r.data?.token) {
            companyTokenStorage.set(r.data.token);
            setCompany(r.data.company);
            return null;
        }
        return r.error ?? "Не удалось войти. Попробуйте ещё раз.";
    }, []);

    const register = useCallback(
        async (body: {
            name: string;
            email: string;
            password: string;
            phone: string;
            contactName: string;
            inn?: string | null;
            ogrn?: string | null;
            kpp?: string | null;
        }): Promise<string | null> => {
            const r = await registerCompany(body);
            if (r.data?.token) {
                companyTokenStorage.set(r.data.token);
                setCompany(r.data.company);
                return null;
            }
            return r.error ?? "Не удалось зарегистрироваться. Попробуйте ещё раз.";
        },
        [],
    );

    const logout = useCallback(() => {
        companyTokenStorage.clear();
        setCompany(null);
    }, []);

    return (
        <CompanyAuthContext.Provider
            value={{
                company,
                loading,
                isAuthenticated: company !== null,
                login,
                register,
                logout,
                setCompany,
            }}
        >
            {children}
        </CompanyAuthContext.Provider>
    );
}

export function useCompanyAuth() {
    const context = useContext(CompanyAuthContext);
    if (context === undefined) {
        throw new Error("useCompanyAuth must be used within a CompanyAuthProvider");
    }
    return context;
}
